import {
  HISTORY_MAX_BLOB_BYTES,
  deriveHistoryKey,
  encryptHistoryArchiveWithKey,
  validateHistoryArchive,
  type HistoryArchive,
} from "@agentvault/crypto";
import sodium from "libsodium-wrappers-sumo";

export const HISTORY_RESTORE_TIMEOUT_MS = 10_000;
/**
 * Three maximum 512 KiB envelopes expand to this many canonical base64
 * bytes, plus 16 KiB for the fixed wrapper and bounded generation metadata.
 */
export const HISTORY_RESTORE_RESPONSE_MAX_BYTES =
  3 * Math.ceil(HISTORY_MAX_BLOB_BYTES / 3) * 4 + 16 * 1024;
const HISTORY_ENVELOPE_MIN_BYTES = 4 + 16 + 24 + 16;
const HISTORY_AAD_PREFIX = "agentvault-history-v1:";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const GENERATION_RESPONSE_KEYS = [
  "id",
  "archive_version",
  "backup_blob",
  "blob_size",
  "entry_count",
  "created_at",
] as const;
const utf8Encoder = new TextEncoder();
const utf8Decoder = new TextDecoder("utf-8", { fatal: true });

export interface HistoryGeneration {
  id: string;
  archive_version: 1;
  backup_blob: string;
  blob_size: number;
  entry_count: number;
  created_at: string;
}

export interface HistoryRestoreResult {
  hubId: string;
  generationId: string;
  generationCreatedAt: string;
  entries: import("./types.js").HistoryEntry[];
  saltHex: string;
}

interface HistoryKeyCacheEntry {
  cacheKey: string;
  pending: Promise<Uint8Array>;
  key?: Uint8Array;
  leases: number;
  evicted: boolean;
  zeroized: boolean;
}

interface HistoryKeyLease {
  key: Uint8Array;
  release: () => void;
}

export class HistoryBackupClient {
  private historyKeyCache?: HistoryKeyCacheEntry;

  constructor(
    private apiUrl: string,
    private deviceJwt: () => string,
    private fetchImpl: typeof fetch = fetch,
    private monotonicNow: () => number = () => performance.now(),
  ) {}

  async upload(input: {
    codeHashHex: string;
    blob: Uint8Array;
    entryCount: number;
    signal?: AbortSignal;
  }): Promise<{ generationId: string }> {
    const response = await this.fetchImpl(
      `${this.apiUrl.replace(/\/+$/, "")}/api/v1/agent-history-backups`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${this.deviceJwt()}`,
        },
        body: JSON.stringify({
          code_hash: input.codeHashHex,
          archive_version: 1,
          backup_blob: Buffer.from(input.blob).toString("base64"),
          entry_count: input.entryCount,
        }),
        signal: input.signal,
      },
    );
    if (!response.ok) throw new Error(`History backup upload failed (HTTP ${response.status})`);
    const body = await response.json() as { generation_id?: unknown };
    if (typeof body.generation_id !== "string") {
      throw new Error("History backup upload returned an invalid response");
    }
    return { generationId: body.generation_id };
  }

  /** Encrypt an upload while leasing the ref-counted `(code hash, salt)` key. */
  async encryptArchive(input: {
    archive: HistoryArchive;
    code: string;
    codeHashHex: string;
    salt: Uint8Array;
  }): Promise<{ blob: Uint8Array; saltHex: string }> {
    const salt = new Uint8Array(input.salt);
    if (salt.length !== 16) throw new Error("Invalid history backup salt");
    const saltHex = Buffer.from(salt).toString("hex");
    const lease = await this.acquireHistoryKey(input.code, input.codeHashHex, saltHex, salt);
    try {
      return {
        blob: await encryptHistoryArchiveWithKey(input.archive, lease.key, salt),
        saltHex,
      };
    } finally {
      lease.release();
    }
  }

  async restore(_input: {
    code: string;
    codeHashHex: string;
    signal: AbortSignal;
  }): Promise<HistoryRestoreResult | null> {
    const input = _input;
    const deadline = this.monotonicNow() + HISTORY_RESTORE_TIMEOUT_MS;
    const controller = new AbortController();
    const abortForDeadline = () => {
      if (!controller.signal.aborted) {
        controller.abort(new DOMException("History restore timed out", "TimeoutError"));
      }
    };
    const checkpoint = () => {
      controller.signal.throwIfAborted();
      if (this.monotonicNow() >= deadline) {
        abortForDeadline();
        throw controller.signal.reason;
      }
    };
    const abortFromCaller = () => controller.abort(input.signal.reason);
    if (input.signal.aborted) abortFromCaller();
    else input.signal.addEventListener("abort", abortFromCaller, { once: true });
    const timeout = setTimeout(abortForDeadline, HISTORY_RESTORE_TIMEOUT_MS);
    try {
      checkpoint();
      const response = await waitForAbortable(this.fetchImpl(
        `${this.apiUrl.replace(/\/+$/, "")}/api/v1/agent-history-backups/restore`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.deviceJwt()}`,
          },
          body: JSON.stringify({ code_hash: input.codeHashHex }),
          signal: controller.signal,
        },
      ), controller.signal);
      checkpoint();
      if (!response.ok) throw new Error(`History restore failed (HTTP ${response.status})`);
      checkpoint();
      const raw = await readBoundedRestoreJson(response, controller.signal, checkpoint);
      checkpoint();
      const wrapper = parseRestoreResponse(raw);
      checkpoint();
      if (wrapper.generations.length === 0) {
        checkpoint();
        return null;
      }

      const failedIds: string[] = [];
      for (const generation of wrapper.generations) {
        let keyLease: HistoryKeyLease | undefined;
        try {
          checkpoint();
          const blob = decodeStrictBase64(generation.backup_blob);
          checkpoint();
          if (blob.length !== generation.blob_size) throw new Error("blob size mismatch");
          assertHistoryEnvelope(blob, checkpoint);
          const salt = blob.slice(4, 20);
          const saltHex = Buffer.from(salt).toString("hex");
          checkpoint();
          const acquisition = this.acquireHistoryKey(input.code, input.codeHashHex, saltHex, salt)
            .then((lease) => {
              if (controller.signal.aborted) {
                lease.release();
                throw controller.signal.reason;
              }
              // Transfer ownership before resolving `acquisition`. An abort
              // can run in the microtask gap before waitForAbortable delivers
              // the value; the surrounding finally must already own it.
              keyLease = lease;
              return lease;
            });
          keyLease = await waitForAbortable(
            acquisition,
            controller.signal,
          );
          checkpoint();
          const archive = await waitForAbortable(
            decryptHistoryArchiveWithKey(blob, keyLease.key, wrapper.hub_id, checkpoint),
            controller.signal,
          );
          checkpoint();
          return {
            hubId: wrapper.hub_id,
            generationId: generation.id,
            generationCreatedAt: generation.created_at,
            entries: archive.entries,
            saltHex,
          };
        } catch (error) {
          checkpoint();
          failedIds.push(generation.id);
        } finally {
          keyLease?.release();
        }
      }
      checkpoint();
      throw new Error(`Unable to restore history generations: ${failedIds.join(", ")}`);
    } finally {
      clearTimeout(timeout);
      input.signal.removeEventListener("abort", abortFromCaller);
    }
  }

  /** Zeroize the active cached key after current restore users release it. */
  clearHistoryKeyCache(): void {
    const entry = this.historyKeyCache;
    if (!entry) return;
    this.historyKeyCache = undefined;
    entry.evicted = true;
    this.zeroizeEvictedKey(entry);
  }

  /** Lifecycle alias for callers tearing down this client. */
  dispose(): void {
    this.clearHistoryKeyCache();
  }

  private async acquireHistoryKey(
    code: string,
    codeHashHex: string,
    saltHex: string,
    salt: Uint8Array,
  ): Promise<HistoryKeyLease> {
    const cacheKey = `${codeHashHex.toLowerCase()}:${saltHex}`;
    let entry = this.historyKeyCache;
    if (!entry || entry.cacheKey !== cacheKey) {
      if (entry) {
        this.historyKeyCache = undefined;
        entry.evicted = true;
        this.zeroizeEvictedKey(entry);
      }
      entry = {
        cacheKey,
        pending: Promise.resolve(new Uint8Array()),
        leases: 0,
        evicted: false,
        zeroized: false,
      };
      const ownedEntry = entry;
      entry.pending = deriveHistoryKey(code, salt).then(
        (key) => {
          ownedEntry.key = key;
          this.zeroizeEvictedKey(ownedEntry);
          return key;
        },
        (error) => {
          ownedEntry.evicted = true;
          if (this.historyKeyCache === ownedEntry) this.historyKeyCache = undefined;
          throw error;
        },
      );
      this.historyKeyCache = entry;
    }

    entry.leases += 1;
    try {
      const key = await entry.pending;
      let released = false;
      return {
        key,
        release: () => {
          if (released) return;
          released = true;
          entry!.leases -= 1;
          this.zeroizeEvictedKey(entry!);
        },
      };
    } catch (error) {
      entry.leases -= 1;
      this.zeroizeEvictedKey(entry);
      throw error;
    }
  }

  private zeroizeEvictedKey(entry: HistoryKeyCacheEntry): void {
    if (!entry.evicted || entry.leases !== 0 || !entry.key || entry.zeroized) return;
    entry.key.fill(0);
    entry.zeroized = true;
  }
}

async function readBoundedRestoreJson(
  response: Response,
  signal: AbortSignal,
  checkpoint: () => void,
): Promise<unknown> {
  const contentLength = response.headers?.get("content-length");
  if (contentLength && /^\d+$/.test(contentLength)) {
    const exceedsCap = BigInt(contentLength) > BigInt(HISTORY_RESTORE_RESPONSE_MAX_BYTES);
    if (exceedsCap) {
      const error = new Error("History restore response is too large");
      void response.body?.cancel(error).catch(() => undefined);
      throw error;
    }
  }
  if (!response.body) throw new Error("History restore returned an invalid response body");

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  let completed = false;
  let cancelled = false;
  const cancel = (reason: unknown) => {
    if (completed || cancelled) return;
    cancelled = true;
    void reader.cancel(reason).catch(() => undefined);
  };
  try {
    while (true) {
      checkpoint();
      const part = await waitForAbortable(reader.read(), signal);
      checkpoint();
      if (part.done) {
        completed = true;
        break;
      }
      if (!(part.value instanceof Uint8Array)) {
        throw new Error("History restore returned an invalid response body");
      }
      if (part.value.length > HISTORY_RESTORE_RESPONSE_MAX_BYTES - total) {
        const error = new Error("History restore response is too large");
        cancel(error);
        throw error;
      }
      chunks.push(part.value);
      total += part.value.length;
    }

    checkpoint();
    const bytes = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) {
      checkpoint();
      bytes.set(chunk, offset);
      offset += chunk.length;
    }
    checkpoint();
    let text: string;
    try {
      text = utf8Decoder.decode(bytes);
    } catch {
      throw new Error("History restore returned invalid UTF-8");
    }
    checkpoint();
    try {
      return JSON.parse(text) as unknown;
    } catch {
      throw new Error("History restore returned invalid JSON");
    }
  } catch (error) {
    cancel(error);
    throw error;
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // A non-cooperative stream may still have a pending read after cancel.
      // Cancellation was requested above; do not mask the bounded-read error.
    }
  }
}

function waitForAbortable<T>(operation: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) return Promise.reject(signal.reason);
  return new Promise<T>((resolve, reject) => {
    const abort = () => reject(signal.reason);
    signal.addEventListener("abort", abort, { once: true });
    operation.then(
      (value) => {
        signal.removeEventListener("abort", abort);
        if (signal.aborted) reject(signal.reason);
        else resolve(value);
      },
      (error) => {
        signal.removeEventListener("abort", abort);
        reject(error);
      },
    );
  });
}

async function decryptHistoryArchiveWithKey(
  blob: Uint8Array,
  key: Uint8Array,
  expectedHubId: string,
  checkpoint: () => void,
): Promise<HistoryArchive> {
  assertHistoryEnvelope(blob, checkpoint);
  await sodium.ready;
  checkpoint();
  let plaintext: Uint8Array;
  try {
    checkpoint();
    plaintext = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
      null,
      blob.slice(44),
      utf8Encoder.encode(`${HISTORY_AAD_PREFIX}${expectedHubId}`),
      blob.slice(20, 44),
      key,
    );
    checkpoint();
  } catch {
    checkpoint();
    throw new Error("Incorrect history recovery code or corrupt history archive");
  }
  let parsed: unknown;
  try {
    checkpoint();
    parsed = JSON.parse(utf8Decoder.decode(plaintext));
    checkpoint();
  } catch {
    checkpoint();
    throw new Error("Incorrect history recovery code or corrupt history archive");
  }
  const archive = validateHistoryArchive(parsed, expectedHubId);
  checkpoint();
  const canonical = utf8Encoder.encode(JSON.stringify(archive));
  checkpoint();
  if (canonical.length !== plaintext.length || canonical.some((byte, index) => byte !== plaintext[index])) {
    throw new Error("History archive plaintext is not canonical JSON");
  }
  checkpoint();
  return archive;
}

function assertHistoryEnvelope(blob: Uint8Array, checkpoint: () => void): void {
  checkpoint();
  if (
    blob.length < HISTORY_ENVELOPE_MIN_BYTES
    || blob.length > HISTORY_MAX_BLOB_BYTES
    || blob[0] !== 0x41
    || blob[1] !== 0x56
    || blob[2] !== 0x48
    || blob[3] !== 0x31
  ) {
    throw new Error("Incorrect history recovery code or corrupt history archive");
  }
  checkpoint();
}

function parseRestoreResponse(value: unknown): { hub_id: string; generations: HistoryGeneration[] } {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("History restore returned an invalid response");
  }
  const record = value as Record<string, unknown>;
  if (
    Object.keys(record).length !== 2
    || !Object.hasOwn(record, "hub_id")
    || !Object.hasOwn(record, "generations")
    || typeof record.hub_id !== "string"
    || !UUID_RE.test(record.hub_id)
    || !Array.isArray(record.generations)
    || record.generations.length > 3
  ) {
    throw new Error("History restore returned an invalid response");
  }
  const generations = record.generations.map((item) => {
    if (item === null || typeof item !== "object" || Array.isArray(item)) {
      throw new Error("History restore returned an invalid response");
    }
    const generation = item as Record<string, unknown>;
    if (
      Object.keys(generation).length !== GENERATION_RESPONSE_KEYS.length
      || !GENERATION_RESPONSE_KEYS.every((key) => Object.hasOwn(generation, key))
      || typeof generation.id !== "string"
      || !UUID_RE.test(generation.id)
      || generation.archive_version !== 1
      || typeof generation.backup_blob !== "string"
      || !Number.isSafeInteger(generation.blob_size)
      || (generation.blob_size as number) < 0
      || (generation.blob_size as number) > HISTORY_MAX_BLOB_BYTES
      || !Number.isSafeInteger(generation.entry_count)
      || (generation.entry_count as number) < 0
      || (generation.entry_count as number) > 500
      || typeof generation.created_at !== "string"
      || Number.isNaN(Date.parse(generation.created_at))
    ) {
      throw new Error("History restore returned an invalid response");
    }
    return generation as unknown as HistoryGeneration;
  });
  return { hub_id: record.hub_id, generations };
}

function decodeStrictBase64(value: string): Uint8Array {
  if (
    value.length === 0
    || value.length > Math.ceil(HISTORY_MAX_BLOB_BYTES / 3) * 4
    || value.length % 4 !== 0
    || !/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(value)
  ) {
    throw new Error("Invalid history backup encoding");
  }
  const decoded = Buffer.from(value, "base64");
  if (decoded.toString("base64") !== value) throw new Error("Invalid history backup encoding");
  return new Uint8Array(decoded);
}
